import { create } from 'zustand';
import { Flight, FlightSchema } from '../types/flight';
import { collection, doc, getDocs, writeBatch, deleteDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';

type FlightsState = {
  flights: Record<string, Flight>;
  loading: boolean;
  error: string | null;
  fetchFlights: () => Promise<void>;
  uploadFlights: (data: FlightSchema[]) => Promise<{ message: string }>;
  updateFlight: (flightId: string, updates: Partial<Flight>) => Promise<void>;
  deleteAll: () => Promise<void>;
};

const FLIGHTS_COLLECTION = 'Flights';

export const useFlightsStore = create<FlightsState>((set) => ({
  flights: {},
  loading: false,
  error: null,

  fetchFlights: async () => {
    set({ loading: true, error: null });
    try {
      // Set up real-time listener
      const unsubscribe = onSnapshot(
        collection(firestore, FLIGHTS_COLLECTION),
        (snapshot) => {
          const flights: Record<string, Flight> = {};
          snapshot.forEach((doc) => {
            flights[doc.id] = {
              ...doc.data(),
              id: doc.id
            } as Flight;
          });
          set({ flights, loading: false });
        },
        (error) => {
          console.error('Error in flights listener:', error);
          set({ error: error.message, loading: false });
        }
      );

      return unsubscribe;
    } catch (error: any) {
      console.error('Error fetching flights:', error);
      set({ error: error.message, loading: false });
    }
  },

  uploadFlights: async (flightsData: FlightSchema[]) => {
    set({ loading: true, error: null });
    try {
      const batch = writeBatch(firestore);
      const createdFlights: Flight[] = [];

      for (const flightData of flightsData) {
        const docRef = doc(collection(firestore, FLIGHTS_COLLECTION));
        const flight: Flight = {
          ...flightData,
          id: docRef.id,
          createdAt: Date.now(),
          updatedAt: Date.now()
        };
        
        batch.set(docRef, flight);
        createdFlights.push(flight);
      }

      await batch.commit();

      set((state) => ({
        flights: {
          ...state.flights,
          ...createdFlights.reduce((acc, flight) => {
            acc[flight.id] = flight;
            return acc;
          }, {} as Record<string, Flight>)
        },
        loading: false
      }));

      return {
        message: `تم إضافة ${createdFlights.length} رحلة بنجاح`
      };
    } catch (error: any) {
      console.error('Error uploading flights:', error);
      set({ error: error.message, loading: false });
      throw new Error('فشل في رفع بيانات الرحلات');
    }
  },

  updateFlight: async (flightId: string, updates: Partial<Flight>) => {
    set({ loading: true, error: null });
    try {
      const flightRef = doc(firestore, FLIGHTS_COLLECTION, flightId);
      
      // Add updatedAt timestamp
      const updatedData = {
        ...updates,
        updatedAt: Date.now()
      };
      
      await updateDoc(flightRef, updatedData);
      
      // Update local state
      set((state) => ({
        flights: {
          ...state.flights,
          [flightId]: {
            ...state.flights[flightId],
            ...updatedData
          }
        },
        loading: false
      }));
    } catch (error: any) {
      console.error('Error updating flight:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteAll: async () => {
    set({ loading: true, error: null });
    try {
      const querySnapshot = await getDocs(collection(firestore, FLIGHTS_COLLECTION));
      const batch = writeBatch(firestore);
      
      querySnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      set({ flights: {}, loading: false });
    } catch (error: any) {
      console.error('Error deleting all flights:', error);
      set({ error: error.message, loading: false });
      throw new Error('فشل في حذف جميع الرحلات');
    }
  }
}));